import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Grid, Card, CardContent, CardActions, Button,
    TextField, MenuItem, IconButton, Dialog, DialogTitle, DialogContent,
    DialogActions, Chip, Autocomplete, Alert, CircularProgress, Divider, Tooltip
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Delete as DeleteIcon,
    PictureAsPdf as PdfIcon,
    Image as ImageIcon,
    Description as FileIcon,
    Share as ShareIcon,
    Download as DownloadIcon,
    Search as SearchIcon,
    Mail as MailIcon,
    Close as CloseIcon,
    Visibility as VisibilityIcon
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

const CATEGORIAS = [
    { value: 'RECIPE', label: 'Récipes', color: 'primary' },
    { value: 'INFORME', label: 'Informes Médicos', color: 'secondary' },
    { value: 'ESTUDIO_EXTERNO', label: 'Estudios Externos', color: 'warning' },
    { value: 'IMAGEN', label: 'Imágenes', color: 'info' },
    { value: 'OTRO', label: 'Otros Documentos', color: 'default' }
];

const DocumentsPage = () => {
    const navigate = useNavigate();
    
    const [viewOpen, setViewOpen] = useState(false);
    const [selectedDoc, setSelectedDoc] = useState<any>(null);

    // --- FUNCIÓN DE CORRECCIÓN DE URL ---
    // Detecta si la URL ya es completa (Supabase) o relativa (Railway Local)
    const getFileUrl = (fileUrl: string) => {
        if (!fileUrl) return '';
        if (fileUrl.startsWith('http')) return fileUrl; // Si ya tiene protocolo, devolver tal cual
        return `${API_URL}${fileUrl}`; // Si es ruta relativa (/uploads/...), añadir API_URL
    };
    
    const handleViewDoc = (doc: any) => {
        setSelectedDoc(doc);
        setViewOpen(true);
    };
    
    // Estados
    const [patients, setPatients] = useState<any[]>([]);
    const [selectedPatient, setSelectedPatient] = useState<any | null>(null);
    const [documents, setDocuments] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [filterCategory, setFilterCategory] = useState('TODOS');

    // Estado Modal Upload
    const [openUpload, setOpenUpload] = useState(false);
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [uploadData, setUploadData] = useState({
        title: '',
        category: 'OTRO',
        fileDate: new Date().toISOString().split('T')[0]
    });
    const [uploading, setUploading] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);

    // 1. Cargar lista de pacientes al inicio
    useEffect(() => {
        const fetchPatients = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${API_URL}/api/patients`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) setPatients(await res.json());
            } catch (e) { console.error(e); }
        };
        fetchPatients();
    }, []);

    // 2. Cargar documentos cuando se selecciona paciente
    useEffect(() => {
        if (selectedPatient) {
            fetchDocuments();
        } else {
            setDocuments([]);
        }
    }, [selectedPatient, filterCategory]);

    const fetchDocuments = async () => {
        if (!selectedPatient) return;
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            let url = `${API_URL}/api/documents?patientId=${selectedPatient.id}`;
            if (filterCategory !== 'TODOS') url += `&category=${filterCategory}`;

            const res = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) setDocuments(await res.json());
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    // 3. Manejar Subida
    const handleUpload = async () => {
        if (!uploadFile || !selectedPatient || !uploadData.title) return;

        setUploading(true);
        const formData = new FormData();
        formData.append('file', uploadFile);
        formData.append('patientId', selectedPatient.id);
        formData.append('title', uploadData.title);
        formData.append('category', uploadData.category);
        formData.append('fileDate', uploadData.fileDate);

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/documents`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if (res.ok) {
                setOpenUpload(false);
                setUploadFile(null);
                setUploadData({ title: '', category: 'OTRO', fileDate: new Date().toISOString().split('T')[0] });
                fetchDocuments(); // Recargar lista
            } else {
                alert('Error al subir archivo');
            }
        } catch (e) {
            console.error(e);
            alert('Error de conexión');
        } finally {
            setUploading(false);
        }
    };

    // 4. Manejar Borrado
    const handleDelete = async (id: string) => {
        if (!window.confirm('¿Eliminar este documento permanentemente?')) return;
        try {
            const token = localStorage.getItem('token');
            await fetch(`${API_URL}/api/documents/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            fetchDocuments();
        } catch (e) { console.error(e); }
    };

    // 5. Enviar por Email Interno (Puente con MailboxPage)
    const handleEmail = (doc: any) => {
        const fullUrl = getFileUrl(doc.fileUrl); // Obtenemos la URL de Supabase
        navigate('/dashboard', { 
            state: { 
                pendingAttachment: fullUrl, 
                fileName: doc.title,
                recipientEmail: selectedPatient?.email || '',
                isFromCloud: true // 👈 Identificador para el Dashboard
            } 
        });
    };

    // Compartir (WhatsApp Externo)
    const handleShare = (doc: any) => {
        const text = `Hola, te comparto el documento: ${doc.title}.`;
        // CORRECCIÓN: Usar getFileUrl para evitar doble protocolo
        const url = getFileUrl(doc.fileUrl);
        window.open(`https://wa.me/?text=${encodeURIComponent(text + " Descargar: " + url)}`, '_blank');
    };

    // Helpers Visuales
    const getIcon = (type: string) => {
        if (type.includes('pdf')) return <PdfIcon sx={{ fontSize: 40, color: '#d32f2f' }} />;
        if (type.includes('image')) return <ImageIcon sx={{ fontSize: 40, color: '#1976d2' }} />;
        return <FileIcon sx={{ fontSize: 40, color: '#757575' }} />;
    };

    const getCategoryColor = (cat: string) => {
        const found = CATEGORIAS.find(c => c.value === cat);
        return found ? found.color : 'default';
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#1976d2', fontWeight: 'bold' }}>
                📂 Gestión Documental
            </Typography>

            {/* BARRA SUPERIOR: BUSCADOR PACIENTE */}
            <Paper sx={{ p: 3, mb: 3, borderLeft: '5px solid #1976d2' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            options={patients}
                            getOptionLabel={(option) => `${option.name} ${option.lastName || ''} (${option.cedula || 'S/C'})`}
                            value={selectedPatient}
                            onChange={(event, newValue) => setSelectedPatient(newValue)}
                            renderInput={(params) => (
                                <TextField 
                                    {...params} 
                                    label="Buscar Paciente" 
                                    placeholder="Nombre o Cédula..." 
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <>
                                                <SearchIcon color="action" sx={{ mr: 1 }} />
                                                {params.InputProps.startAdornment}
                                            </>
                                        )
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        {selectedPatient && (
                            <Button 
                                variant="contained" 
                                color="primary" 
                                startIcon={<CloudUploadIcon />}
                                onClick={() => setOpenUpload(true)}
                            >
                                Importar Documento
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            {/* CONTENIDO PRINCIPAL */}
            {selectedPatient ? (
                <>
                    {/* FILTROS */}
                    <Box sx={{ mb: 3 }}>
                        <Chip 
                            label="Todos" 
                            clickable 
                            color={filterCategory === 'TODOS' ? 'primary' : 'default'} 
                            onClick={() => setFilterCategory('TODOS')}
                            sx={{ mr: 1 }}
                        />
                        {CATEGORIAS.map(cat => (
                            <Chip 
                                key={cat.value}
                                label={cat.label} 
                                clickable 
                                // @ts-ignore
                                color={filterCategory === cat.value ? cat.color : 'default'} 
                                onClick={() => setFilterCategory(cat.value)}
                                sx={{ mr: 1 }}
                            />
                        ))}
                    </Box>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
                    ) : documents.length === 0 ? (
                        <Alert severity="info">No hay documentos cargados para este paciente en esta categoría.</Alert>
                    ) : (
                        <Grid container spacing={3}>
                            {documents.map(doc => (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={doc.id}>
                                    <Card sx={{ 
                                        height: '100%', 
                                        display: 'flex', 
                                        flexDirection: 'column',
                                        transition: 'transform 0.2s',
                                        '&:hover': { transform: 'scale(1.02)', boxShadow: 6 }
                                    }}>
                                        <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                                            <Box sx={{ mb: 2 }}>{getIcon(doc.fileType)}</Box>
                                            
                                            <Typography variant="h6" component="div" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                                                {doc.title}
                                            </Typography>
                                            
                                            <Chip 
                                                label={CATEGORIAS.find(c => c.value === doc.category)?.label || doc.category} 
                                                size="small" 
                                                // @ts-ignore
                                                color={getCategoryColor(doc.category)}
                                                variant="outlined"
                                                sx={{ mb: 1 }}
                                            />
                                            
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                Fecha: {new Date(doc.fileDate).toLocaleDateString()}
                                            </Typography>
                                        </CardContent>
                                        <Divider />
                                        <CardActions sx={{ justifyContent: 'space-around', bgcolor: '#f5f5f5' }}>
                                            <Tooltip title="Ver en pantalla">
                                                <IconButton color="primary" onClick={() => handleViewDoc(doc)}>
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Tooltip>
                                            
                                            <Tooltip title="Enviar por Email">
                                                <IconButton color="secondary" onClick={() => handleEmail(doc)}>
                                                    <MailIcon />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Compartir (WhatsApp)">
                                                <IconButton color="success" onClick={() => handleShare(doc)}>
                                                    <ShareIcon />
                                                </IconButton>
                                            </Tooltip>
                                            
                                            <Tooltip title="Eliminar">
                                                <IconButton color="error" onClick={() => handleDelete(doc.id)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </>
            ) : (
                <Box sx={{ textAlign: 'center', mt: 5, opacity: 0.6 }}>
                    <SearchIcon sx={{ fontSize: 80, color: '#ccc' }} />
                    <Typography variant="h6" color="text.secondary">
                        Seleccione un paciente para gestionar sus documentos
                    </Typography>
                </Box>
            )}

            {/* MODAL DE IMPORTACIÓN */}
            <Dialog open={openUpload} onClose={() => setOpenUpload(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Importar Documento</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1 }}>
                        <TextField 
                            label="Título del Documento" 
                            fullWidth 
                            margin="normal"
                            value={uploadData.title}
                            onChange={e => setUploadData({...uploadData, title: e.target.value})}
                            placeholder="Ej: Resonancia Magnética de Rodilla"
                        />
                        
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    select
                                    label="Categoría"
                                    fullWidth
                                    margin="normal"
                                    value={uploadData.category}
                                    onChange={e => setUploadData({...uploadData, category: e.target.value})}
                                >
                                    {CATEGORIAS.map(opt => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    type="date"
                                    label="Fecha del Documento"
                                    fullWidth
                                    margin="normal"
                                    InputLabelProps={{ shrink: true }}
                                    value={uploadData.fileDate}
                                    onChange={e => setUploadData({...uploadData, fileDate: e.target.value})}
                                />
                            </Grid>
                        </Grid>

                        <Box 
                            sx={{ 
                                border: '2px dashed #1976d2', 
                                borderRadius: 2, 
                                p: 3, 
                                mt: 2, 
                                textAlign: 'center', 
                                cursor: 'pointer',
                                bgcolor: uploadFile ? '#e3f2fd' : 'transparent',
                                '&:hover': { bgcolor: '#f5f5f5' }
                            }}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <input 
                                type="file" 
                                hidden 
                                ref={fileInputRef} 
                                onChange={e => {
                                    if(e.target.files?.[0]) {
                                        const file = e.target.files[0];
                                        setUploadFile(file);
                                        if(!uploadData.title) {
                                            setUploadData(prev => ({...prev, title: file.name.split('.')[0]}));
                                        }
                                    }
                                }} 
                            />
                            {uploadFile ? (
                                <Typography color="primary" fontWeight="bold">
                                    📄 {uploadFile.name}
                                </Typography>
                            ) : (
                                <>
                                    <CloudUploadIcon sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
                                    <Typography>Haga clic para seleccionar o arrastre aquí</Typography>
                                    <Typography variant="caption" color="text.secondary">PDF, JPG, PNG (Máx 10MB)</Typography>
                                </>
                            )}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenUpload(false)}>Cancelar</Button>
                    <Button 
                        variant="contained" 
                        onClick={handleUpload} 
                        disabled={!uploadFile || !uploadData.title || uploading}
                    >
                        {uploading ? 'Subiendo...' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* VISOR DE DOCUMENTOS INTEGRADO - CORREGIDO */}
            <Dialog 
                open={viewOpen} 
                onClose={() => setViewOpen(false)} 
                fullWidth 
                maxWidth="lg"
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#1976d2', color: 'white' }}>
                    <Typography variant="h6">{selectedDoc?.title || 'Visualizador'}</Typography>
                    <IconButton onClick={() => setViewOpen(false)} sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0, height: '80vh', display: 'flex', justifyContent: 'center', bgcolor: '#525659' }}>
                    {selectedDoc && (
                        selectedDoc.fileType.includes('pdf') ? (
                            <iframe
                                // CORRECCIÓN: Usar getFileUrl para evitar doble protocolo
                                src={`${getFileUrl(selectedDoc.fileUrl)}#toolbar=0`}
                                width="100%"
                                height="100%"
                                style={{ border: 'none' }}
                                title="Visor PDF"
                            />
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', overflow: 'auto' }}>
                                <img 
                                    // CORRECCIÓN: Usar getFileUrl para evitar doble protocolo
                                    src={getFileUrl(selectedDoc.fileUrl)} 
                                    alt={selectedDoc.title} 
                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} 
                                />
                            </Box>
                        )
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewOpen(false)}>Cerrar</Button>
                    <Button 
                        variant="contained" 
                        startIcon={<DownloadIcon />}
                        // CORRECCIÓN: Usar getFileUrl para evitar doble protocolo
                        href={getFileUrl(selectedDoc?.fileUrl)}
                        target="_blank"
                        download
                    >
                        Descargar Archivo
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DocumentsPage;